import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { History, RotateCcw, Trash2 } from "lucide-react";
import { fmt$, fmt$2 } from "@/lib/format";
import { incomeBasis } from "@/lib/incomePlanner";

// Timeline of every accepted weight state (baseline / AI / manual). Users
// can pin any two to see them compared, restore an older version, or delete.
export function SnapshotHistory({ snapshots, onRestore, onDelete }) {
  const [pinned, setPinned] = useState([]); // array of snapshot ids, max 2
  const togglePin = (sid) => {
    setPinned((prev) => {
      if (prev.includes(sid)) return prev.filter((x) => x !== sid);
      if (prev.length >= 2) return [prev[1], sid]; // drop oldest, keep last 2
      return [...prev, sid];
    });
  };
  const clearPins = () => setPinned([]);
  const pinnedSnapshots = pinned.map((sid) => snapshots.find((s) => s.id === sid)).filter(Boolean);

  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <Card className="border-primary/20" data-testid="snapshot-history">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-primary flex items-center gap-2">
              <History className="w-3.5 h-3.5" /> Optimizer history
            </div>
            <h3 className="serif text-2xl mt-2">Before & after · versioned snapshots</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Every AI rebalance is saved so you can compare &ldquo;before AI&rdquo; vs &ldquo;after AI&rdquo; months later, or restore any version with one click. Pin any two snapshots to see them side-by-side.
            </p>
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {snapshots.length} version{snapshots.length === 1 ? "" : "s"}
            {pinned.length > 0 && (
              <> · <span className="text-secondary">{pinned.length} pinned</span></>
            )}
          </div>
        </div>

        {pinnedSnapshots.length === 2 && (
          <SnapshotCompare a={pinnedSnapshots[0]} b={pinnedSnapshots[1]} onClose={clearPins} />
        )}

        <div className="space-y-2" data-testid="snapshot-list">
          {snapshots.map((s) => (
            <div key={s.id} className="rounded-md border border-border bg-card px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
                    s.source === "ai_optimizer" ? "bg-secondary/15 text-secondary" :
                    s.source === "baseline" ? "bg-muted text-foreground/70" :
                    "bg-primary/10 text-primary"
                  }`}>
                    {s.source === "ai_optimizer" ? "AI" : s.source === "baseline" ? "Baseline" : "Manual"}
                  </span>
                  <span className="serif text-lg">{s.label}</span>
                  <span className="mono text-[10px] text-muted-foreground">{fmtTime(s.created_at)}</span>
                </div>
                {s.rationale && (
                  <p className="text-xs text-muted-foreground italic mt-1 leading-relaxed">
                    &ldquo;{s.rationale}&rdquo;
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-1">
                  {(s.holdings || []).map((h) => (
                    <span key={h.ticker} className="mono text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-foreground/80 border border-border">
                      {h.ticker} <span className="text-secondary">{Number(h.weight).toFixed(1)}%</span>
                    </span>
                  ))}
                </div>
                {s.summary && (
                  <div className="mt-2 flex flex-wrap gap-4 mono text-[10px] text-muted-foreground">
                    <span>YoC <span className="text-foreground">{Number(s.summary.yield_on_cost_pct).toFixed(2)}%</span></span>
                    <span>CAGR <span className="text-foreground">{Number(s.summary.cagr_pct).toFixed(2)}%</span></span>
                    <span>Final <span className="text-foreground">${Number(s.summary.final_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></span>
                    <span>Est. income/mo <span className="text-foreground">${incomeBasis(s.summary).monthly.toFixed(2)}</span></span>
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  variant={pinned.includes(s.id) ? "default" : "outline"}
                  size="sm"
                  onClick={() => togglePin(s.id)}
                  data-testid={`snapshot-pin-${s.id}`}
                  className={`rounded-full ${pinned.includes(s.id) ? "bg-secondary text-secondary-foreground hover:bg-secondary/90" : ""}`}
                  aria-pressed={pinned.includes(s.id)}
                >
                  {pinned.includes(s.id) ? "Pinned" : "Pin"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRestore(s.id)}
                  data-testid={`snapshot-restore-${s.id}`}
                  className="rounded-full"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(s.id)}
                  data-testid={`snapshot-delete-${s.id}`}
                  aria-label="Delete snapshot"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// Two-column comparison used when the user pins exactly two snapshots.
function SnapshotCompare({ a, b, onClose }) {
  const monthly = (s) => incomeBasis(s).monthly;
  const sa = a.summary || {}, sb = b.summary || {};
  const bars = [
    { key: "Yield on cost", va: sa.yield_on_cost_pct, vb: sb.yield_on_cost_pct, fmt: (v) => `${Number(v || 0).toFixed(2)}%` },
    { key: "CAGR",           va: sa.cagr_pct,           vb: sb.cagr_pct,           fmt: (v) => `${Number(v || 0).toFixed(2)}%` },
    { key: "Total return",   va: sa.total_return_pct,   vb: sb.total_return_pct,   fmt: (v) => `${Number(v || 0).toFixed(2)}%` },
    { key: "Final value",    va: sa.final_value,        vb: sb.final_value,        fmt: (v) => fmt$(v || 0) },
    { key: "Monthly income", va: monthly(sa), vb: monthly(sb), fmt: (v) => fmt$2(v || 0) },
  ];
  const maxFor = (row) => Math.max(Math.abs(row.va || 0), Math.abs(row.vb || 0), 0.0001);

  const holdingUnion = Array.from(new Set([...(a.holdings || []).map((h) => h.ticker), ...(b.holdings || []).map((h) => h.ticker)]));
  const wA = new Map((a.holdings || []).map((h) => [h.ticker, Number(h.weight)]));
  const wB = new Map((b.holdings || []).map((h) => [h.ticker, Number(h.weight)]));

  return (
    <Card className="border-secondary/40 bg-secondary/[0.03]" data-testid="snapshot-compare">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-secondary">Snapshot compare</div>
            <h4 className="serif text-xl mt-1">
              <span className="text-primary">{a.label}</span> <span className="text-muted-foreground">vs</span> <span className="text-primary">{b.label}</span>
            </h4>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="snapshot-compare-close">
            Clear pins
          </Button>
        </div>

        {/* Metric bars */}
        <div className="space-y-3">
          {bars.map((row) => {
            const max = maxFor(row);
            const widthA = ((row.va || 0) / max) * 100;
            const widthB = ((row.vb || 0) / max) * 100;
            const winnerA = (row.va || 0) >= (row.vb || 0);
            return (
              <div key={row.key} className="rounded-md border border-border bg-card px-4 py-3">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{row.key}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${winnerA ? "bg-primary" : "bg-primary/40"}`} style={{ width: `${Math.max(6, widthA)}%` }} />
                    </div>
                    <div className={`serif text-lg mt-1 ${winnerA ? "text-primary" : "text-foreground"}`}>{row.fmt(row.va)}</div>
                    <div className="mono text-[10px] text-muted-foreground truncate">{a.label}</div>
                  </div>
                  <div className="text-right">
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ml-auto ${!winnerA ? "bg-secondary" : "bg-secondary/40"}`} style={{ width: `${Math.max(6, widthB)}%` }} />
                    </div>
                    <div className={`serif text-lg mt-1 ${!winnerA ? "text-secondary" : "text-foreground"}`}>{row.fmt(row.vb)}</div>
                    <div className="mono text-[10px] text-muted-foreground truncate">{b.label}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Weight diff table */}
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm" data-testid="snapshot-compare-weights">
            <thead className="bg-muted/60">
              <tr className="text-left">
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Ticker</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">{a.label}</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">{b.label}</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              {holdingUnion.map((tk) => {
                const av = wA.get(tk); const bv = wB.get(tk);
                const delta = (bv ?? 0) - (av ?? 0);
                return (
                  <tr key={tk} className="border-t border-border">
                    <td className="px-3 py-1.5 mono font-semibold">{tk}</td>
                    <td className="px-3 py-1.5 mono text-xs text-right">{av == null ? "—" : `${av.toFixed(2)}%`}</td>
                    <td className="px-3 py-1.5 mono text-xs text-right">{bv == null ? "—" : `${bv.toFixed(2)}%`}</td>
                    <td className={`px-3 py-1.5 mono text-xs text-right ${delta >= 0 ? "text-primary" : "text-destructive"}`}>
                      {delta >= 0 ? "+" : ""}{delta.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
